import React, { useState, useRef, useLayoutEffect, useEffect, useCallback, useId } from 'react';
import { createPortal } from 'react-dom';
import { GLOSSARY } from './infoGlossary';
import './InfoTip.css';

/* ------------------------------------------------------------------ *
 * Uitleg-tooltip. Wikkelt precies één element (kaart, label, kolomkop,
 * knop) en laat bij hover, toetsenbord-focus of tik/klik een uitleg zien.
 *
 *   <InfoTip k="sharpe"><div className="equity-card">…</div></InfoTip>
 *   <InfoTip info={{ title, what, … }} extra="Nu: 12,3%">…</InfoTip>
 *
 * De uitleg komt uit infoGlossary.js (of `info`). De bubbel wordt in
 * document.body getekend met position: fixed en blijft altijd binnen het
 * venster: horizontaal geklemd, en onder of boven het element, afhankelijk
 * van waar de meeste ruimte is. Klik/tik zet de bubbel vast (handig op
 * touch); Escape, nogmaals klikken of buiten klikken sluit hem.
 * ------------------------------------------------------------------ */

const MARGIN = 8;
const GAP = 8;
const MAX_WIDTH = 380;

const DIRECTION = {
  higher: ['▲', 'Hoger is beter'],
  lower: ['▼', 'Lager is beter'],
  zero: ['◎', 'Dichter bij 0% is beter'],
  within: ['◎', 'Binnen je limiet blijven'],
  info: ['ℹ', 'Ter informatie'],
};

function Bubble({ info, extra, id, bubbleRef, style }) {
  const [arrow, label] = DIRECTION[info.direction] || [];
  return (
    <div id={id} role="tooltip" ref={bubbleRef} className="infotip-bubble" style={style}>
      {info.title && <div className="infotip-title">{info.title}</div>}
      {info.node ? info.node : (
        <>
          {info.what && <p><strong>Wat is het?</strong> {info.what}</p>}
          {info.direction && (
            <p className={`infotip-direction infotip-direction-${info.direction}`}>
              <span aria-hidden="true">{arrow}</span> <strong>{label}</strong>{info.good ? ` — ${info.good}` : ''}
            </p>
          )}
          {!info.direction && info.good && <p><strong>Goed of slecht?</strong> {info.good}</p>}
          {info.example && <p className="infotip-example"><strong>Voorbeeld:</strong> {info.example}</p>}
          {info.benchmark && <p><strong>Marktrichtlijn:</strong> {info.benchmark}</p>}
          {info.note && <p className="infotip-note"><strong>Let op:</strong> {info.note}</p>}
        </>
      )}
      {extra && <p className="infotip-extra">{extra}</p>}
      {!info.node && info.benchmark && <p className="infotip-disclaimer">Indicatieve richtlijnen, geen beleggingsadvies.</p>}
    </div>
  );
}

export default function InfoTip({ k, info, extra, children }) {
  const content = info || GLOSSARY[k];
  const child = React.Children.only(children);
  const id = useId();
  const anchorRef = useRef(null);
  const bubbleRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [style, setStyle] = useState({ visibility: 'hidden', top: 0, left: 0 });

  const close = useCallback(() => { setOpen(false); setPinned(false); }, []);

  // Positioneren: pas nadat de bubbel is gemeten, zodat we exact weten hoe groot hij is.
  useLayoutEffect(() => {
    if (!open || !anchorRef.current || !bubbleRef.current) return;
    const a = anchorRef.current.getBoundingClientRect();
    const vw = document.documentElement.clientWidth;
    const vh = window.innerHeight;
    const width = Math.min(MAX_WIDTH, vw - 2 * MARGIN);
    const height = bubbleRef.current.offsetHeight;

    const left = Math.max(MARGIN, Math.min(a.left + a.width / 2 - width / 2, vw - width - MARGIN));
    const spaceBelow = vh - a.bottom - GAP - MARGIN;
    const spaceAbove = a.top - GAP - MARGIN;
    const below = height <= spaceBelow || spaceBelow >= spaceAbove;
    const available = Math.max(80, below ? spaceBelow : spaceAbove);
    const shown = Math.min(height, available);
    const top = below ? a.bottom + GAP : a.top - GAP - shown;

    setStyle({ visibility: 'visible', left, top: Math.max(MARGIN, top), width, maxHeight: available });
  }, [open, content, extra]);

  // Sluiten bij Escape, scrollen/formaat wijzigen, of buiten klikken (tik op touch).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    const onOutside = (e) => {
      if (anchorRef.current?.contains(e.target) || bubbleRef.current?.contains(e.target)) return;
      close();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    document.addEventListener('pointerdown', onOutside);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
      document.removeEventListener('pointerdown', onOutside);
    };
  }, [open, close]);

  if (!content) return child;

  const props = child.props;
  const show = () => { setStyle((s) => ({ ...s, visibility: 'hidden' })); setOpen(true); };
  const clone = React.cloneElement(child, {
    ref: anchorRef,
    'data-infotip': 'true',
    tabIndex: props.tabIndex ?? 0,
    'aria-describedby': open ? id : props['aria-describedby'],
    onMouseEnter: (e) => { props.onMouseEnter?.(e); show(); },
    onMouseLeave: (e) => { props.onMouseLeave?.(e); if (!pinned) setOpen(false); },
    onFocus: (e) => { props.onFocus?.(e); show(); },
    onBlur: (e) => { props.onBlur?.(e); if (!pinned) setOpen(false); },
    onClick: (e) => {
      props.onClick?.(e);
      if (pinned) close(); else { show(); setPinned(true); }
    },
  });

  return (
    <>
      {clone}
      {open && createPortal(
        <Bubble info={content} extra={extra} id={id} bubbleRef={bubbleRef} style={style} />,
        document.body,
      )}
    </>
  );
}
